import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Chips } from '@cian/ui-kit';
import { debounce } from '@cian/newbuilding-utils';

import { roomsDescriptor } from '../../constants/descriptors';
import { isDescriptorActive } from '../../utils/filters';
import { IApplicationState } from '../../types/redux';
import { TTypedThunkDispatch } from '../../types/thunk';
import { getOffersCount, getOffersData } from '../../actions/offersData';
import { updateTags } from '../../actions/filterTags';
import { selectCanUseHiddenBase } from '../../selectors/user';
import { FILTER_SELECTION_TIMEOUT } from '../../constants/timeouts';

interface IRoomFilterProps {
  /**
   * Если true, фильтр применяется немедленно без нажатия кнопки SearchButton
   */
  autoApply?: boolean;
}

export const RoomFilter: React.FC<IRoomFilterProps> = React.memo(({ autoApply = false }) => {
  const dispatch = useDispatch<TTypedThunkDispatch>();
  const jsonQuery = useSelector((state: IApplicationState) => state.jsonQuery);
  const canUseHiddenBase = useSelector(selectCanUseHiddenBase);

  const applyFilter = React.useCallback(() => {
    dispatch(getOffersData({ canUseHiddenBase }));
  }, [dispatch, canUseHiddenBase]);

  const applyFilterDebounced = React.useRef(debounce(FILTER_SELECTION_TIMEOUT, applyFilter));

  React.useEffect(() => {
    applyFilterDebounced.current = debounce(FILTER_SELECTION_TIMEOUT, applyFilter);
  }, [applyFilter]);

  const options = React.useMemo(
    () =>
      roomsDescriptor.map(({ state, shortLabel }) => ({
        label: shortLabel as string,
        value: state,
      })),
    [],
  );

  const activeDescriptors = React.useMemo(
    () => roomsDescriptor.filter(descriptor => isDescriptorActive(descriptor)(jsonQuery)),
    [jsonQuery],
  );

  const currentValues = React.useMemo(() => activeDescriptors.map(d => d.state), [activeDescriptors]);

  const handleChipClick = React.useCallback(
    (value: string) => {
      const descriptor = roomsDescriptor.find(d => d.state === value);

      if (descriptor) {
        dispatch({
          type: `${descriptor.name}/${descriptor.state}`,
          value,
        });
        dispatch(updateTags());

        if (autoApply) {
          applyFilterDebounced.current();
        } else {
          dispatch(getOffersCount());
        }
      }
    },
    [dispatch, autoApply],
  );

  return <Chips options={options} size="M" value={currentValues} selectType="multiple" onChipClick={handleChipClick} />;
});

RoomFilter.displayName = 'RoomFilter';
